import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OptimizationService } from '../services/optimization/OptimizationService';
import { OPTIMIZATION_STATUS } from '../services/optimization/constants';

export function useOptimization(planningId) {
  const [currentRun, setCurrentRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  const hasActiveStatus = useMemo(() => {
    return status === OPTIMIZATION_STATUS.QUEUED || status === OPTIMIZATION_STATUS.RUNNING;
  }, [status]);

  const loadLatest = useCallback(async () => {
    if (!planningId) return;
    setLoading(true);
    try {
      const run = await OptimizationService.getLatestRun(planningId);
      setCurrentRun(run);
      setStatus(run?.status || null);
    } finally {
      setLoading(false);
    }
  }, [planningId]);

  useEffect(() => {
    loadLatest();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadLatest]);

  // Poll API for status while a run exists
  useEffect(() => {
    if (!planningId || !currentRun) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await OptimizationService.fetchRunStatusFromAPI(planningId, currentRun);
      if (s && s !== status) setStatus(s);
    }, 5000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [planningId, currentRun, status]);

  const startOptimization = useCallback(
    async ({ optimizationFile, createdBy }) => {
      if (!planningId) return null;
      const { id: runId } = await OptimizationService.createOptimizationRun(planningId, {
        optimizationFile,
        createdBy,
      });
      await loadLatest();
      return runId;
    },
    [planningId, loadLatest]
  );

  const applyResults = useCallback(async () => {
    if (!planningId || !currentRun) return null;
    const result = await OptimizationService.fetchRunResult(planningId, currentRun);
    return result;
  }, [planningId, currentRun]);

  return {
    loading,
    status,
    currentRun,
    hasActiveStatus,
    startOptimization,
    applyResults,
    refresh: loadLatest,
  };
}
